import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { SystemHealthService } from 'app/api/system-health.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreConfigService } from '@core/services/config.service';

@Component({
  selector: 'app-stats-info',
  templateUrl: './stats-info.component.html',
  styleUrls: ['./stats-info.component.scss']
})
export class StatsInfoComponent implements OnInit, OnDestroy {
  @Input() isCenter: boolean = false;
  public systemHealth: any = null;
  public showStats = false;
  private destroy$ = new Subject<void>();
  private intervalId: any = null;

  constructor(
    private systemHealthService: SystemHealthService,
    private _coreConfigService: CoreConfigService
  ) { }

  ngOnInit(): void {
    this._coreConfigService.config.pipe(takeUntil(this.destroy$)).subscribe(config => {
      this.showStats = config?.app?.appName === 'E1-VMS';
      if (this.showStats && !this.intervalId) {
        this.loadSystemHealth();
        this.intervalId = setInterval(() => {
          this.loadSystemHealth();
        }, 10000);
      } else if (!this.showStats && this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    });
  }

  loadSystemHealth(): void {
    this.systemHealthService.getSystemHealth()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.systemHealth = res;
        },
        error: (err) => {
          console.error("Failed to load system health in stats-info:", err);
        }
      });
  }

  formatBytes(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const dm = 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  getStorageUsage(): string {
    if (!this.systemHealth?.storage) return '70%';
    const usedPct = 100 - this.systemHealth.storage.free_pct;
    return `${usedPct.toFixed(0)}%`;
  }

  getStorageTitle(): string {
    if (!this.systemHealth?.storage) return 'Storage Usage';
    const used = this.formatBytes(this.systemHealth.storage.used_bytes);
    const total = this.formatBytes(this.systemHealth.storage.total_bytes);
    const usedPct = (100 - this.systemHealth.storage.free_pct).toFixed(1);
    return `Disk Used: ${used} / ${total} (${usedPct}%)`;
  }

  getCameraStats(): string {
    if (!this.systemHealth?.cameras) return '15/24';
    const active = this.systemHealth.cameras.by_state?.STREAMING || 0;
    const total = this.systemHealth.cameras.total || 0;
    return `${active}/${total}`;
  }

  getCameraTitle(): string {
    if (!this.systemHealth?.cameras) return 'Camera Status';
    const active = this.systemHealth.cameras.by_state?.STREAMING || 0;
    const offline = this.systemHealth.cameras.offline_count || 0;
    return `Cameras Streaming: ${active} | Offline: ${offline}`;
  }

  getRxSpeed(): string {
    if (!this.systemHealth?.network || this.systemHealth.network.length === 0) return '146';
    const net = this.systemHealth.network[0];
    return (net.rx_mbps || 0).toFixed(0);
  }

  getTxSpeed(): string {
    if (!this.systemHealth?.network || this.systemHealth.network.length === 0) return '44';
    const net = this.systemHealth.network[0];
    return (net.tx_mbps || 0).toFixed(0);
  }

  getNetworkTitle(): string {
    if (!this.systemHealth?.network || this.systemHealth.network.length === 0) return 'Network Speed';
    const net = this.systemHealth.network[0];
    return `Net Rx: ${net.rx_mbps?.toFixed(1)} Mbps | Tx: ${net.tx_mbps?.toFixed(1)} Mbps`;
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}

